import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
// eslint-disable-next-line import/no-extraneous-dependencies
import dayjs from "dayjs";
import clsx from "clsx";
import isoWeek from "dayjs/plugin/isoWeek";
import Icon from "components/Icon/Icon";
import { Expandable } from "common/components/Expandable/Expandable";
import getAnimalLabel from "utils/animalLabel";
import LegendChart, { chartCategories, getChartColors } from "./LegendChart";

dayjs.extend(isoWeek);

const getWeekRange = (date: string) => {
  const startOfWeek = dayjs(date).startOf("isoWeek"); // Lundi de la semaine
  const endOfWeek = dayjs(date).endOf("isoWeek"); // Dimanche de la semaine

  return {
    start: startOfWeek.format("DD/MM/YYYY"),
    end: endOfWeek.format("DD/MM/YYYY"),
  };
};

function EvolutionChart({
  evolution,
  evolutionBySection,
  isEvolutionChartVisible,
  isSectionChartVisible,
  areSubEventsVisible,
  areSubEnrichmentsVisible,
  areSubEnclosureUpdatesVisible,
}: any) {
  const { t } = useTranslation();

  const getFormat = useCallback((scale: any, date: any) => {
    switch (scale) {
      case "hour":
        return `De ${dayjs(date).format("HH")}h à ${dayjs(date)
          .add(1, "hour")
          .format("HH")}h`;
      case "day":
        return dayjs(date).format("L");
      case "week":
        return `${getWeekRange(date).start}-${getWeekRange(date).end}`;
      case "month":
        return dayjs(date).format("MMM YYYY");
      case "year":
        return dayjs(date).year();

      default:
        return "LL";
    }
  }, []);

  const barsData = useMemo(() => {
    if (!evolution) return [];
    const { data, scale } = evolution;

    if (!data || data.length < 1) {
      return [];
    }

    return (
      Object?.entries(data)?.map(([date, values]) => ({
        label: getFormat(scale, date),
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        ...values,
      })) || []
    );
  }, [evolution, getFormat]);

  return isEvolutionChartVisible || isSectionChartVisible ? (
    <div className="rounded-2xl border border-slate-200 flex flex-col gap-8 p-8 flex-1">
      {isEvolutionChartVisible && barsData && barsData.length > 0 ? (
        <h3 className="text-xl font-semibold">{t("balance.evolutionTitle")}</h3>
      ) : null}

      {isEvolutionChartVisible && (
        <div className="w-full flex flex-col">
          <LegendChart />
          <div className="flex flex-col mt-12 gap-2">
            {barsData && barsData.length > 0 ? (
              barsData?.map((bar, index) => {
                const rowChart = (
                  <div
                    // eslint-disable-next-line react/no-array-index-key
                    key={`bar-${index}`}
                    className={clsx("flex flex-row items-center flex-1")}
                  >
                    <div className="w-[30%]">
                      <span className="font-semibold text-xl">{bar.label}</span>
                    </div>

                    <div className="flex-1 rounded-xl overflow-hidden h-6 flex flex-row ">
                      {Object?.entries(chartCategories)?.map(
                        ([cat, _], catIndex) => {
                          return bar[cat] > 0 ? (
                            <div
                              style={{
                                backgroundColor: getChartColors(cat).background,
                                color: getChartColors(cat).color,
                                width: `${bar[cat]}%`,
                              }}
                              className={clsx(
                                "text-center flex justify-center items-center font-semibold min-w-[35px]",
                                catIndex > 0 && "border-l border-white"
                              )}
                            >
                              {bar[cat]}
                              <small>%</small>
                            </div>
                          ) : null;
                        }
                      )}
                    </div>
                  </div>
                );
                return (bar.events?.length ||
                  bar.enclosureUpdates?.length ||
                  bar.enrichments?.length) &&
                  (areSubEnclosureUpdatesVisible ||
                    areSubEnrichmentsVisible ||
                    areSubEventsVisible) ? (
                  <Expandable
                    titleComponent={rowChart}
                    content={
                      <div className="flex flex-col gap-4">
                        {areSubEventsVisible &&
                          bar.events?.map((event: any) => {
                            return (
                              <div
                                key={event.id}
                                className="flex flex-row items-center gap-2"
                              >
                                <Icon name="Megaphone" className="w-6 h-6" />
                                <p>
                                  <strong>
                                    {event?.enclosure
                                      ? event?.enclosure?.name
                                      : event?.animal
                                      ? getAnimalLabel(
                                          event.animal?.id,
                                          event.animal?.houseName,
                                          event.animal?.zimsId,
                                          event.animal?.localId,
                                          event.animal?.tag
                                        )
                                      : event?.observationGroup
                                      ? `${t("common.individualsGroup")}: ${
                                          event?.observationGroup?.name
                                        }`
                                      : null}
                                  </strong>{" "}
                                  {event.shortDescription}{" "}
                                  <span className="italic">
                                    ({dayjs(event.date ?? event.createdAt).format("LLL")})
                                  </span>
                                </p>
                              </div>
                            );
                          })}
                        {areSubEnclosureUpdatesVisible &&
                          bar.enclosureUpdates?.map((enclosureUpdate: any) => {
                            return (
                              <div
                                key={enclosureUpdate.id}
                                className="flex flex-row items-center gap-2"
                              >
                                <Icon
                                  name="PencilSquareIcon"
                                  className="w-6 h-6"
                                />
                                <p>
                                  {enclosureUpdate.type === "start"
                                    ? "Début : "
                                    : enclosureUpdate.type === "end"
                                    ? "Fin : "
                                    : ""}
                                  {enclosureUpdate.title}{" "}
                                  <span className="italic">
                                    (
                                    {enclosureUpdate.type === "start"
                                      ? dayjs(
                                          enclosureUpdate.actualStartDateTime
                                        ).format("LL")
                                      : enclosureUpdate.type === "end"
                                      ? dayjs(
                                          enclosureUpdate.actualEndDateTime
                                        ).format("LL")
                                      : `Du ${dayjs(
                                          enclosureUpdate.actualStartDateTime
                                        ).format("ll")} au ${dayjs(
                                          enclosureUpdate.actualEndDateTime
                                        ).format("ll")}`}
                                    )
                                  </span>
                                </p>
                              </div>
                            );
                          })}
                        {areSubEnrichmentsVisible &&
                          bar.enrichments?.map((enclosureUpdate: any) => {
                            return (
                              <div
                                key={enclosureUpdate.id}
                                className="flex flex-row items-center gap-2"
                              >
                                <Icon name="BsStar" className="w-6 h-6" />
                                <p>
                                  {enclosureUpdate.type === "start"
                                    ? "Début : "
                                    : enclosureUpdate.type === "end"
                                    ? "Fin : "
                                    : ""}
                                  {enclosureUpdate.enrichmentRequestTitle}{" "}
                                  <span className="italic">
                                    (
                                    {enclosureUpdate.type === "start"
                                      ? dayjs(
                                          enclosureUpdate.startDateTime
                                        ).format("LL")
                                      : enclosureUpdate.type === "end"
                                      ? dayjs(
                                          enclosureUpdate.endDateTime
                                        ).format("LL")
                                      : `Du ${dayjs(
                                          enclosureUpdate.startDateTime
                                        ).format("ll")} au ${dayjs(
                                          enclosureUpdate.endDateTime
                                        ).format("ll")}`}
                                    )
                                  </span>
                                </p>
                              </div>
                            );
                          })}
                      </div>
                    }
                  />
                ) : (
                  rowChart
                );
              })
            ) : (
              <div>
                <span>{t(`common.noDataToShow`)}</span>
              </div>
            )}
          </div>
        </div>
      )}
      {isSectionChartVisible &&
        evolution &&
        Array.isArray(evolutionBySection) &&
        evolutionBySection?.map((section: any, i: number) => {
          const getChartData = () => {
            const { data, scale } = section.evolution;

            if (!data || data.length < 1) {
              return [];
            }

            return (
              Object?.entries(data)?.map(([date, values]) => ({
                label: getFormat(scale, date),
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                ...values,
              })) || []
            );
          };

          const chartData = getChartData();

          return (
            <div
              key={section.title}
              className={clsx(
                "flex flex-col gap-8",
                (isEvolutionChartVisible || i > 0) &&
                  "border-t-2 border-t-slate-200 pt-4 "
              )}
            >
              <h3 className="text-xl font-semibold">
                {t("forms.evolutionOfSection", { section: section.title })}
              </h3>

              <div className="w-full flex flex-col">
                <LegendChart />
                <div className="flex flex-col mt-12 gap-2">
                  {chartData && chartData.length > 0 ? (
                    chartData?.map((bar, index) => {
                      const rowChart = (
                        <div
                          // eslint-disable-next-line react/no-array-index-key
                          key={`bar-${index}`}
                          className={clsx("flex flex-row items-center flex-1")}
                        >
                          <div className="w-[30%]">
                            <span className="font-semibold text-xl">
                              {bar.label}
                            </span>
                          </div>

                          <div className="flex-1 rounded-xl overflow-hidden h-6 flex flex-row ">
                            {Object?.entries(chartCategories)?.map(
                              ([cat, _], catIndex) => {
                                return bar[cat] > 0 ? (
                                  <div
                                    style={{
                                      backgroundColor:
                                        getChartColors(cat).background,
                                      color: getChartColors(cat).color,
                                      width: `${bar[cat]}%`,
                                    }}
                                    className={clsx(
                                      "text-center flex justify-center items-center font-semibold min-w-[35px]",
                                      catIndex > 0 && "border-l border-white"
                                    )}
                                  >
                                    {bar[cat]}
                                    <small>%</small>
                                  </div>
                                ) : null;
                              }
                            )}
                          </div>
                        </div>
                      );
                      return rowChart;
                    })
                  ) : (
                    <div>
                      <span>{t(`common.noDataToShow`)}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
    </div>
  ) : null;
}

export default EvolutionChart;
